/**
 * MONSTER TOWN — GAME CONTEXT + APP STATE. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * `ctx` is how a crew reaches the renderer, camera, touch router and world
 * graph. It is created once in `main.ts` and handed to every crew's `install()`.
 * Do NOT reach across crews for anything else — use the event bus.
 *
 * `app` is the small amount of truly global state: which mode we are in, which
 * phase we are showing, where the player is. Everything else belongs to a crew.
 */

import type { WorldGraph } from '../core/scene'
import type { Engine } from '../core/engine'
import type { IsoCamera } from '../render/camera'
import type { GameRenderer } from '../render/renderer'
import type { TouchRouter } from '../input/touch'
import { bus } from '../core/events'
import { DEFAULT_MONSTER, type GameMode, type MonsterConfig, type Phase, type SceneId, type Vec3 } from '../core/types'

export interface GameContext {
  readonly canvas: HTMLCanvasElement
  readonly hud: HTMLElement
  readonly renderer: GameRenderer
  readonly camera: IsoCamera
  readonly touch: TouchRouter
  readonly world: WorldGraph
  readonly engine: Engine
}

/**
 * A crew's entry point. Register systems, subscribe to the bus, build art.
 * Called once, after core is up and content is registered.
 */
export type CrewInstall = (ctx: GameContext) => void

class AppState {
  private _mode: GameMode = 'play'
  private _phase: Phase = 'boot'

  /** Player monster look. Owned by the monster crew, read by everyone. */
  monster: MonsterConfig = { ...DEFAULT_MONSTER }
  /** Player world position in the ACTIVE scene. Monster crew writes this. */
  playerPos: Vec3 = { x: 0, y: 0, z: 0 }
  /** Scene the player considers "home" — the door they came out of. */
  homeSceneId: SceneId = 'town' as SceneId

  get mode(): GameMode {
    return this._mode
  }

  get phase(): Phase {
    return this._phase
  }

  /** The one hammer toggle. Same camera in both modes. */
  setMode(mode: GameMode): void {
    if (mode === this._mode) return
    const previous = this._mode
    this._mode = mode
    bus.emit('app:modeChanged', { mode, previous })
  }

  toggleMode(): void {
    this.setMode(this._mode === 'build' ? 'play' : 'build')
  }

  setPhase(phase: Phase): void {
    if (phase === this._phase) return
    const previous = this._phase
    this._phase = phase
    bus.emit('app:phaseChanged', { phase, previous })
  }
}

export const app = new AppState()
